/**
 * CLAIMGUARD CANONICAL FINANCIAL VOCABULARY — FROZEN (governance freeze).
 *
 * Six — and only six — customer-facing financial calculations exist in the
 * ClaimGuard application, report, dashboard, emails, PDFs and negotiation
 * documents. Every surface MUST consume the canonical output of
 * `buildReportFromBundle()` (src/lib/report.ts) and MUST label it with the
 * exact strings below. No component may recreate a formula or invent an
 * alternate user-facing label.
 *
 * Enforced by src/lib/report/financial-vocabulary.test.ts.
 */

export const FINANCIAL_LABELS = {
  /** A. Total amount billed on the original invoice. Source: report.invoiceTotal */
  invoiceTotal: "Invoice Total",
  /** B. Defensible market value of the work performed. Source: report.fairRange */
  estimatedFairPayment: "Estimated Fair Payment",
  /** C. Recovery supported by market benchmark evidence. */
  marketBasedRecovery: "Market-Based Recovery",
  /** D. Recovery supported by statutory / documentation findings. */
  nonMarketRecovery: "Non-Market Recovery",
  /** E. C + D. Source: report.estimatedSavings (canonical, never recomputed). */
  totalSupportedRecovery: "Total Supported Recovery",
  /** F. Invoice Total − Total Supported Recovery. */
  settlementGoal: "Settlement Goal",
} as const;

export type FinancialLabelKey = keyof typeof FINANCIAL_LABELS;

/** Per-line phrasing rules (frozen). */
export const FINANCIAL_PHRASES = {
  /** Rule 1 — per-line benchmark reduction. */
  marketLine: (amount: string) => `Market recovery up to ${amount}`,
  /** Rule 2 — per-line non-market finding. */
  nonMarketLine: (amount: string) => `Non-market recovery up to ${amount}`,
  /** Rule 3 — a finding amount that is only part of the total. */
  findingAmount: "Supported recovery",
  /** Rule 4 — the largest individual argument. */
  largestArgument: "Largest single recovery",
  /** Rule 5 — midpoints must name themselves. */
  midpoint: "Midpoint of Total Supported Recovery",
  /** Rule 6 — empty state. */
  empty: "No supported recovery identified",
} as const;

/**
 * Labels retired by the Financial Outcome Consistency Certification.
 * Reintroducing any of these is a regression and fails the test suite.
 */
export const DEPRECATED_FINANCIAL_LABELS: readonly string[] = [
  "Recovery Opportunity",
  "Combined Recovery",
  "Combined Recovery Range",
  "Estimated Savings",
  "Estimated Savings Opportunity",
  "Potential Savings",
  "Savings Range",
  "Potential Reduction",
  "Estimated Negotiation Opportunity",
  "Estimated Savings Range",
  "Fair Market Range",
  "Estimated Fair Value",
  "Expected Reasonable Amount",
  "Recommended Payment",
  "Invoice Submitted",
  "Total Invoice Reviewed",
];
